
import React from 'react';
import { Load } from './types';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Check, Clock, X } from 'lucide-react';
import { getLoadIcon } from './utils/loadIcons';

interface LoadStatusTableProps {
  loads: Load[];
  handleToggleStatus: (id: string) => void;
  openScheduling: (load: Load) => void;
}

const LoadStatusTable: React.FC<LoadStatusTableProps> = ({ 
  loads, 
  handleToggleStatus, 
  openScheduling 
}) => {
  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-0.5 mb-6">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Load</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Location</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Power</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Control</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Schedule</th>
            </tr>
          </thead>
          <tbody>
            {loads.map((load) => (
              <tr key={load.id} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    {getLoadIcon(load.type)}
                    <span className="ml-2 text-gray-800 dark:text-gray-200 font-medium">{load.name}</span>
                  </div>
                </td>
                <td className="px-4 py-4 text-gray-600 dark:text-gray-400">{load.location}</td>
                <td className="px-4 py-4 text-gray-600 dark:text-gray-400">{load.powerRating}</td>
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      load.status 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {load.status ? <Check className="mr-1 h-3 w-3" /> : <X className="mr-1 h-3 w-3" />}
                      {load.status ? 'ON' : 'OFF'}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <Switch 
                    checked={load.status} 
                    onCheckedChange={() => handleToggleStatus(load.id)} 
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openScheduling(load)}
                      className="text-xs h-8 bg-white dark:bg-transparent border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <Clock className="mr-1 h-3 w-3" />
                      {load.schedules.length > 0 ? `${load.schedules.length} Schedule(s)` : 'Add Schedule'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LoadStatusTable;
